export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.29.151:8003/api';

// Simple cache to avoid repeated API calls
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (url, options) => {
  return `${options.method || 'GET'}_${url}`;
};

const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    ...options.headers,
  };
  
  // Don't set Content-Type for FormData (let browser set it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Check cache for GET requests only
  const cacheKey = getCacheKey(url, options);
  if (!options.method || options.method === 'GET') {
    const cached = cache.get(cacheKey);
    if (cached && isCacheValid(cached.timestamp)) {
      return cached.data;
    }
  }
  
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers,
    ...options,
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
      return;
    }
    
    let errorMessage = 'Request failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    
    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }
  
  const data = await response.json();
  
  // Cache GET requests
  if (!options.method || options.method === 'GET') {
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  } else {
    // Clear related cache on mutations
    const urlParts = url.split('/');
    const resource = urlParts[1]; // e.g., 'workers', 'attendance'
    for (const key of cache.keys()) {
      if (key.includes(resource)) {
        cache.delete(key);
      }
    }
  }
  
  return data;
};

const api = {
  auth: {
    login: (data) => apiRequest('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    register: (data) => apiRequest('/auth/register', { method: 'POST', body: JSON.stringify(data) })
  },
  users: {
    getAll: () => apiRequest('/users'),
    create: (data) => apiRequest('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/users/${id}`, { method: 'DELETE' }),
    assignRole: (userId, data) => apiRequest(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify(data) })
  },
  workers: {
    getAll: () => apiRequest('/workers'),
    create: (data) => apiRequest('/workers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/workers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/workers/${id}`, { method: 'DELETE' }),
    getById: (id) => apiRequest(`/workers/${id}`),
    getNextUnid: () => apiRequest('/workers/next-unid')
  },
  attendance: {
    getAll: () => apiRequest('/attendance'),
    create: (data) => apiRequest('/attendance', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/attendance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/attendance/${id}`, { method: 'DELETE' })
  },
  leaves: {
    getAll: () => apiRequest('/leaves'),
    create: (data) => apiRequest('/leaves', { method: 'POST', body: JSON.stringify(data) }),
    updateStatus: (id, status) => apiRequest(`/leaves/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) })
  },
  leaveTypes: {
    getAll: () => apiRequest('/leave-types'),
    create: (data) => apiRequest('/leave-types', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/leave-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/leave-types/${id}`, { method: 'DELETE' })
  },
  holidays: {
    getAll: () => apiRequest('/holidays'),
    create: (data) => apiRequest('/holidays', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/holidays/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/holidays/${id}`, { method: 'DELETE' })
  },
  shifts: {
    getAll: () => apiRequest('/shifts'),
    create: (data) => apiRequest('/shifts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/shifts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/shifts/${id}`, { method: 'DELETE' })
  },
  payroll: {
    getAll: () => apiRequest('/payroll'),
    create: (data) => apiRequest('/payroll', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/payroll/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/payroll/${id}`, { method: 'DELETE' }),
    getWorkerAttendance: (worker_id, startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return apiRequest(`/payroll/worker-attendance/${worker_id}${queryString ? '?' + queryString : ''}`);
    }
  },
  processStages: {
    getAll: () => apiRequest('/process-stages'),
    create: (data) => apiRequest('/process-stages', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/process-stages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/process-stages/${id}`, { method: 'DELETE' })
  },
  countries: {
    getAll: () => apiRequest('/countries')
  },
  states: {
    getAll: () => apiRequest('/states')
  },
  cities: {
    getAll: () => apiRequest('/cities')
  },
  companyIdTypes: {
    getAll: () => apiRequest('/company-id-types')
  },
  categories: {
    getAll: () => apiRequest('/categories')
  },
  stoneTypes: {
    getAll: () => apiRequest('/stone-types')
  },
  currencies: {
    getAll: () => apiRequest('/currencies')
  },
  diamondTypes: {
    getAll: () => apiRequest('/diamond-types')
  },
  userPermissions: {
    getByUserId: (userId) => apiRequest(`/user-permissions/user/${userId}`),
    create: (data) => apiRequest('/user-permissions', { method: 'POST', body: JSON.stringify(data) }),
    update: (userId, data) => apiRequest(`/user-permissions/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (userId) => apiRequest(`/user-permissions/${userId}`, { method: 'DELETE' })
  },
  roles: {
    getAll: () => apiRequest('/roles'),
    create: (data) => apiRequest('/roles', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/roles/${id}`, { method: 'DELETE' }),
    getPermissions: (roleId) => apiRequest(`/roles/${roleId}/permissions`),
    updatePermissions: (roleId, data) => apiRequest(`/roles/${roleId}/permissions`, { method: 'PUT', body: JSON.stringify(data) })
  },
  upload: {
    single: (formData) => apiRequest('/upload/single', { method: 'POST', body: formData }),
    multiple: (formData) => apiRequest('/upload/multiple', { method: 'POST', body: formData }),
    delete: (filename) => apiRequest(`/upload/file/${filename}`, { method: 'DELETE' }),
    getFile: (filename) => `${API_BASE_URL}/upload/file/${filename}`
  },
  paymentTerms: {
    getAll: () => apiRequest('/payment-terms'),
    create: (data) => apiRequest('/payment-terms', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/payment-terms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/payment-terms/${id}`, { method: 'DELETE' })
  }

};

export default api;